/**
 * Sprint 12+I (04.07.2026) — Floating Feedback-Icon fuer Pionier-Seiten.
 *
 * Ersetzt die dominante Feedback-Sektion im Cockpit durch eine dezente,
 * fixed-bottom-right Sprechblase mit Modal (Textarea + Submit). POSTet an
 * `/api/feedback/submit` (existierender Endpoint, `type=feature`).
 */
import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { MessageSquare, X, Loader2, CheckCircle2, Send } from "lucide-react";

export default function PionierFeedbackIcon() {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | success | error
  const [error, setError] = useState("");

  // POSTLAUNCH-V5 · P0-#5 — VersionFooter feuert Custom-Event `f4s:feedback-open`.
  React.useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("f4s:feedback-open", handler);
    return () => window.removeEventListener("f4s:feedback-open", handler);
  }, []);

  const close = () => {
    setOpen(false);
    // State-Reset leicht verzögert (nach Fade-Out).
    setTimeout(() => {
      setText("");
      setState("idle");
      setError("");
    }, 150);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) {
      setError("Ein paar Worte reichen. Was hast du im Kopf?");
      setState("error");
      return;
    }
    setState("submitting");
    try {
      await api.post("/feedback/submit", {
        type: "feature",
        message: text.trim(),
        context: { section: "pionier_feedback_icon" },
      });
      setState("success");
      setTimeout(close, 2000);
    } catch (err) {
      setError(
        err?.response?.data?.detail?.code ||
          "Feedback konnte nicht gesendet werden. Versuch's gleich noch mal."
      );
      setState("error");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Feedback geben"
        className="fixed bottom-6 right-6 z-50 w-12 h-12 rounded-full bg-[#4563BB] hover:bg-[#3a54a2] text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
        data-testid="pionier-feedback-icon-btn"
      >
        <MessageSquare className="w-5 h-5" />
      </button>

      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-slate-900/40 p-4"
          data-testid="pionier-feedback-modal"
          onClick={close}
        >
          <div
            className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={close}
              className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
              aria-label="Schliessen"
              data-testid="pionier-feedback-modal-close"
            >
              <X className="w-4 h-4 text-slate-500" />
            </button>

            {state === "success" ? (
              <div className="text-center py-6" data-testid="pionier-feedback-modal-success">
                <div className="inline-flex w-12 h-12 rounded-full bg-emerald-100 items-center justify-center mb-3">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">Danke!</h3>
                <p className="mt-2 text-sm text-slate-600">Wir lesen dein Feedback.</p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate data-testid="pionier-feedback-modal-form">
                <h3 className="text-lg font-bold text-slate-900">Gib uns Feedback</h3>
                <p className="mt-1 text-sm text-slate-500">
                  Was funktioniert? Was fehlt? Was nervt?
                </p>
                <Textarea
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                    if (state === "error") setState("idle");
                  }}
                  placeholder="Schreib uns kurz ..."
                  className="mt-4 min-h-[100px] text-sm"
                  disabled={state === "submitting"}
                  data-testid="pionier-feedback-modal-textarea"
                />
                {state === "error" && (
                  <p
                    className="mt-2 text-xs text-red-600"
                    data-testid="pionier-feedback-modal-error"
                  >
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  disabled={state === "submitting"}
                  className="mt-4 w-full bg-[#4563BB] hover:bg-[#3a54a2] h-10 text-sm font-semibold"
                  data-testid="pionier-feedback-modal-submit"
                >
                  {state === "submitting" ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Wird gesendet ...
                    </>
                  ) : (
                    <>
                      Senden <Send className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}
